export type Severity = 'low' | 'medium' | 'high'
export type TicketKind = 'incident' | 'improvement' | 'suggestion' | 'not_applicable'
export type MaxDeadlineUnit = 'days' | 'hours'

/** Exibe o ID sequencial do ticket (ex.: #42). */
export function formatTicketNumber(ticketNumber: number | null | undefined): string {
  if (ticketNumber == null) return '—'
  return `#${ticketNumber}`
}

export interface QualityTicketTypeOriginLink {
  ticket_type_id: string
  origin_id: string
  created_at: string
}

export interface QualityTicketType {
  id: string
  name: string
  display_name: string
  description?: string | null
  is_active: boolean
  deleted_at?: string | null
  deleted_by?: string | null
  deleter?: { id: string; name: string } | null
  created_at: string
  updated_at: string
  /** IDs das origens vinculadas a este tipo (preenchido por listTicketTypesWithOrigins). */
  origin_ids?: string[]
  /** Origens vinculadas expandidas (preenchido por listTicketTypesWithOrigins). */
  origins?: Pick<QualityTicketOrigin, 'id' | 'name' | 'display_name'>[]
}

export interface QualityTicketOrigin {
  id: string
  name: string
  display_name: string
  description?: string | null
  is_active: boolean
  deleted_at?: string | null
  deleted_by?: string | null
  deleter?: { id: string; name: string } | null
  created_at: string
  updated_at: string
}

export interface QualityImprocedentReason {
  id: string
  name: string
  display_name: string
  description?: string | null
  is_active: boolean
  deleted_at?: string | null
  deleted_by?: string | null
  deleter?: { id: string; name: string } | null
  created_at: string
  updated_at: string
}

export interface QualityCategory {
  id: string
  name: string
  display_name: string
  description?: string | null
  is_active: boolean
  deleted_at?: string | null
  deleted_by?: string | null
  deleter?: { id: string; name: string } | null
  created_at: string
  updated_at: string
}

export interface QualityTicketStage {
  id: string
  key: string
  name: string
  order_index: number
  is_closed: boolean
  color?: string | null
  deleted_at?: string | null
  deleted_by?: string | null
  deleter?: { id: string; name: string } | null
}

export interface QualityTicket {
  id: string
  /** Número sequencial imutável atribuído pelo banco (1, 2, 3, …). */
  ticket_number?: number
  title: string
  description?: string | null
  /** Justificativa complementar ao registro. Coluna: justification */
  justification?: string | null
  stage_id: string
  category_id?: string | null
  department_id?: string | null
  severity?: Severity | null
  occurred_at?: string | null
  owner_user_id: string
  /** Usuário que cadastrou o ticket (preenchido pelo banco na criação). */
  registered_by_user_id?: string | null
  kind: TicketKind
  requester_user_id?: string | null
  ticket_type_id?: string | null
  origin_id?: string | null
  improcedent_reason_id?: string | null
  external_link?: string | null
  resolution?: string | null
  closed_at?: string | null
  deleted_at?: string | null
  deleted_by?: string | null
  created_at: string
  updated_at: string
  stage?: QualityTicketStage | null
  category?: Pick<QualityCategory, 'id' | 'name' | 'display_name'> | null
  department?: { id: string; name: string; display_name: string } | null
  requester?: {
    id: string
    name: string | null
    department?: { id: string; name?: string; display_name: string } | null
  } | null
  /** Perfil de quem cadastrou (join opcional em listagens). */
  registered_by?: { id: string; name: string | null } | null
  broker_id?: string | null
  broker?: { id: string; name: string; id_scgp: number | null } | null
  /** Número da apólice (texto). Coluna: policy_number */
  policy_number?: string | null
  /** Código do convênio/subgrupo (somente dígitos). Coluna: convenio_code */
  convenio_code?: number | null
  /** Unidade do prazo máximo corrido: dias ou horas. */
  max_deadline_unit?: MaxDeadlineUnit | null
  /** Valor numérico do prazo máximo corrido. */
  max_deadline_value?: number | null
  /** Observações sobre o prazo máximo corrido. */
  max_deadline_notes?: string | null
  ticket_type?: Pick<QualityTicketType, 'id' | 'name' | 'display_name'> | null
  origin?: Pick<QualityTicketOrigin, 'id' | 'name' | 'display_name'> | null
  improcedent_reason?: Pick<QualityImprocedentReason, 'id' | 'name' | 'display_name'> | null
  deleter?: { id: string; name: string } | null
}

/** Corretor resumido para pickers e labels de ticket. */
export interface TicketBroker {
  id: string
  name: string
  id_scgp: number | null
}

export interface QualityTicketComment {
  id: string
  ticket_id: string
  user_id: string
  content: string
  created_at: string
  deleted_at?: string | null
  deleted_by?: string | null
  author?: { id: string; name: string; user_group_id?: string | null } | null
  attachments?: QualityTicketAttachment[]
}

export interface QualityTicketHistory {
  id: string
  ticket_id: string
  changed_by: string | null
  field_changed: string
  old_value: string | null
  new_value: string | null
  changed_at: string
  changer?: { id: string; name: string } | null
}

export interface QualityTicketAttachment {
  id: string
  ticket_id: string
  file_path: string
  file_name: string
  file_size?: number | null
  mime_type?: string | null
  uploaded_by?: string | null
  created_at: string
  comment_id?: string | null
}

export interface QualityParameter {
  id: string
  key: string
  value: string
  description?: string | null
  created_at: string
  updated_at: string
}

// Aliases de compatibilidade (para não quebrar referências externas enquanto a migração acontece)
export type QualityIncidentType    = QualityTicketType
export type QualityIncidentOrigin  = QualityTicketOrigin
export type QualityIncidentStage   = QualityTicketStage
export type QualityIncident        = QualityTicket
export type QualityIncidentComment = QualityTicketComment
export type QualityIncidentHistory = QualityTicketHistory
